import React, { useState } from 'react'
import { connect } from 'react-redux'
import EmailVerificationEmailPasswordForm from '../../../components/modals/emailVerification/EmailVerificationEmailPasswordForm'
import EmailVerificationCodeEntry from '../../../components/modals/emailVerification/EmailVerificationCodeEntry'
import ReauthenticationModal from '../../../components/reauthenticationModal/ReauthenticationModal'
import {
  createEmailVerification, resendEmailVerification, verifyEmailVerification,
} from '../../../actions/emailVerification'
import { showNotification } from '../../../actions/notifications'
import useEmailVerificationRequest from '../../../utils/emailVerification/useEmailVerificationRequest'
import {
  ADD_BACKUP_EMAIL_DESCRIPTION, UPDATE_BACKUP_EMAIL_DESCRIPTION,
  BACKUP_SAME_AS_ACCOUNT_EMAIL_ERROR, BACKUP_SAME_AS_CURRENT_BACKUP_ERROR,
} from '../../../utils/emailVerification/emailVerificationCopy'

// Simple, standalone "add/update a backup email" flow for the account page —
// just email (+ reauth if the session needs it) -> code -> done. This is
// intentionally separate from the bigger email-verification prompt modal.
// Adding and replacing a backup both use the same upsertBackup purpose (the
// existing backup stays live until the new address verifies) — this
// container just adapts its copy based on whether one is already set.
function AddBackupEmailContainer(props) {
  const {
    user, signinMethods, hideModal,
    createEmailVerification, resendEmailVerification, verifyEmailVerification,
    showNotification,
  } = props

  const isUpdating = !!user.backupEmail

  const [step, setStep] = useState('form') // 'form' | 'code'
  const {
    verification, requestCode, requestLoading, requestError,
    showReauth, handleReauthenticate, cancelReauth, submitCode, resend,
  } = useEmailVerificationRequest({
    userId: user.id,
    createEmailVerification,
    resendEmailVerification,
    verifyEmailVerification,
    onCodeSent: () => setStep('code'),
  })

  const handleSubmit = ({ email }) => requestCode({ purpose: 'upsertBackup', email })

  const handleVerifyCode = async (code) => {
    await submitCode(code)
    hideModal()
    showNotification('', isUpdating ? 'Backup email updated' : 'Backup email added', 'default')
  }

  if (step === 'code') {
    return (
      <EmailVerificationCodeEntry
        email={verification.email}
        title={isUpdating ? 'Verify your new backup email address' : 'Verify your backup email address'}
        description={isUpdating ? 'Please enter the code we just sent to your new backup email address' : 'Please enter the code we just sent to your email address'}
        resendAvailableAt={verification.resendAvailableAt}
        showIllustration={false}
        onSubmitCode={handleVerifyCode}
        onResend={resend}
      />
    )
  }

  return (
    <React.Fragment>
      <EmailVerificationEmailPasswordForm
        title={isUpdating ? 'Update backup email address' : 'Add a backup email address'}
        description={isUpdating ? UPDATE_BACKUP_EMAIL_DESCRIPTION : ADD_BACKUP_EMAIL_DESCRIPTION}
        showIllustration={false}
        emailLabel='Backup email address'
        submitLabel={isUpdating ? 'Update backup' : 'Send confirmation code'}
        currentEmail={user.email}
        duplicateEmailError={BACKUP_SAME_AS_ACCOUNT_EMAIL_ERROR}
        currentBackupEmail={user.backupEmail}
        sameAsCurrentBackupError={BACKUP_SAME_AS_CURRENT_BACKUP_ERROR}
        submitLoading={requestLoading}
        errorMessage={requestError}
        onSubmit={handleSubmit}
      />
      {showReauth && (
        <ReauthenticationModal
          title={isUpdating ? 'Update Backup Email Address' : 'Add a Backup Email Address'}
          signinMethodsToDisplay={signinMethods}
          onAuthenticate={handleReauthenticate}
          hideModal={cancelReauth}
        />
      )}
    </React.Fragment>
  )
}

export default connect(
  (state) => ({ user: state.user, signinMethods: state.signinMethods }),
  {
    createEmailVerification, resendEmailVerification, verifyEmailVerification, showNotification,
  },
)(AddBackupEmailContainer)
